import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

const COLORS = ['#ef4444', '#3b82f6', '#22c55e', '#eab308', '#a855f7'];

const GRADIENTS = [
  'linear-gradient(135deg,#f97316,#ec4899)',
  'linear-gradient(135deg,#22c55e,#06b6d4)',
  'linear-gradient(135deg,#6366f1,#a855f7)',
  'linear-gradient(135deg,#eab308,#ef4444)',
  'linear-gradient(135deg,#14b8a6,#84cc16)',
];

const GLYPHS: Record<string, string[]> = {
  flowers: ['🌸', '🌼', '🌺', '🌷', '💐', '🏵️'],
  stars: ['⭐', '✨', '🌟', '💫'],
};

export type ConfettiTheme = 'confetti' | 'flowers' | 'stars' | 'gradient';

function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

interface Props {
  /** Flip/increment this to fire a burst. */
  trigger: number;
  duration?: number;
  /** Visual style of the celebration. */
  theme?: ConfettiTheme;
  /** Layer class; raise it when the burst must sit above a modal dialog. */
  zClass?: string;
}

/**
 * Lightweight Telegram-style confetti burst. Pure CSS animation on a fixed,
 * pointer-events-none overlay so it never blocks quiz interaction. Bursts from
 * the left and right edges, arcs inward, and auto-unmounts when done. Respects
 * reduced-motion.
 */
export function ConfettiBurst({ trigger, duration = 2500, theme = 'confetti', zClass = 'z-[45]' }: Props) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!trigger) return;
    if (prefersReducedMotion()) return;
    setVisible(true);
    // haptic feedback + a short screen shake so the burst is felt, not just seen
    try {
      navigator.vibrate?.([25, 35, 45]);
    } catch { /* unsupported */ }
    const root = document.body;
    root.classList.remove('isc-shake');
    // force reflow so the animation restarts on repeat bursts
    void root.offsetWidth;
    root.classList.add('isc-shake');
    const shakeT = window.setTimeout(() => root.classList.remove('isc-shake'), 500);
    const t = window.setTimeout(() => setVisible(false), duration);
    return () => {
      window.clearTimeout(t);
      window.clearTimeout(shakeT);
      root.classList.remove('isc-shake');
    };
  }, [trigger, duration]);

  const pieces = useMemo(() => {
    const isSmall = typeof window !== 'undefined' && window.innerWidth < 640;
    const glyphs = GLYPHS[theme];
    const base = isSmall ? 70 : 130;
    const count = glyphs ? Math.round(base * 0.65) : base;
    return Array.from({ length: count }, (_, i) => {
      const origin = i % 3 === 0 ? 'top' : i % 3 === 1 ? 'left' : 'right';
      const fromRight = origin === 'right';
      const strip = i % 3 !== 0;
      const w = glyphs ? 14 + Math.random() * 16 : strip ? 4 + Math.random() * 4 : 6 + Math.random() * 5;
      // Drift is expressed in vw so the arc looks identical on phones and desktops.
      const driftMag = 18 + Math.random() * 26;
      return {
        glyph: glyphs ? glyphs[Math.floor(Math.random() * glyphs.length)] : null,
        origin,
        side: fromRight ? ('right' as const) : ('left' as const),
        offset: Math.random() * 90 + 5,
        top: Math.random() * 80 + 10,
        width: w,
        height: glyphs ? w : strip ? w * (2.5 + Math.random() * 2) : w,
        radius: strip ? '1px' : '50%',
        color: theme === 'gradient' ? GRADIENTS[i % GRADIENTS.length] : COLORS[i % COLORS.length],
        lift: origin === 'top' ? `${-(2 + Math.random() * 4)}vh` : `${-(14 + Math.random() * 16)}vh`,
        fall: origin === 'top' ? `${65 + Math.random() * 30}vh` : `${45 + Math.random() * 25}vh`,
        drift:
          origin === 'top'
            ? `${(Math.random() > 0.5 ? 1 : -1) * (4 + Math.random() * 18)}vw`
            : fromRight
              ? `-${driftMag}vw`
              : `${driftMag}vw`,
        spin: `${(Math.random() > 0.5 ? 1 : -1) * (180 + Math.random() * 540)}deg`,
        dur: 1.8 + Math.random() * 0.6,
        // Bursts must feel instant: only a couple of frames of stagger.
        delay: Math.random() * 0.04,
      };
    });
    // regenerate per burst
  }, [trigger, theme]);

  if (!visible || typeof document === 'undefined') return null;

  return createPortal(
    <div
      aria-hidden="true"
      className={`pointer-events-none fixed inset-0 ${zClass} overflow-hidden`}
    >
      {pieces.map((p, i) => (
        <span
          key={i}
          className={`isc-confetti-piece${p.glyph ? ' isc-confetti-glyph' : ''}`}
          style={{
            ...(p.origin === 'top'
              ? { left: `${p.offset}%`, top: '-4%' }
              : { [p.side]: '0', top: `${p.top}%` }),
            ...(p.glyph
              ? { fontSize: `${p.width}px` }
              : {
                  width: `${p.width}px`,
                  height: `${p.height}px`,
                  borderRadius: p.radius,
                  background: p.color,
                }),
            animationDuration: `${p.dur}s`,
            animationDelay: `${p.delay}s`,
            ['--isc-drift' as string]: p.drift,
            ['--isc-spin' as string]: p.spin,
            ['--isc-lift' as string]: p.lift,
            ['--isc-fall' as string]: p.fall,
          }}
        >
          {p.glyph}
        </span>
      ))}
    </div>,
    document.body,
  );
}

export default ConfettiBurst;